import { BusinessPolicyDiscountType } from '../../../modules/business-policies/enums/business-policy-discount-type.enum';
import { BusinessPolicyLogicalOperatorType } from '../../../modules/business-policies/enums/business-policy-logical-operator-type.enum';
import { BusinessPolicySetOperator } from '../../../modules/business-policies/enums/business-policy-set-operator.enum';
import { DiscountApplicationRuleEnum } from '../../../modules/business-policies/enums/discount-application-rule.enum';
import { OrderType } from '../../../modules/orders/interfaces/orderType.enum';
import { StatusType } from '../../../modules/orders/order.types';
import { CompactAvailability } from '../../../modules/products/ProductTypes';
import { UserRole } from '../../../modules/users/roles';
import { PaginatedEndpointParams } from '../../types';
import { BusinessPolicyConditionNodeDTO } from './businessPolicyCondition.types';

export enum BusinessPolicyType {
  ORDER_ITEM_DISCOUNT = 'ORDER_ITEM_DISCOUNT',
  ORDER_WARNING = 'ORDER_WARNING',
  ORDER_RESTRICTION = 'ORDER_RESTRICTION',
  ORDER_VOUCHER = 'ORDER_VOUCHER',
  CASHBACK = 'CASHBACK',
  RELEASE_PAYMENT_METHODS = 'RELEASE_PAYMENT_METHODS',
  ORDER_PRODUCTS_RESTRICTION = 'ORDER_PRODUCTS_RESTRICTION',
}

export enum BusinessPolicyActiveFilter {
  ACTIVE = 'true',
  INACTIVE = 'false',
}

export enum BusinessPolicyBatchAction {
  ACTIVATE = 'activate',
  INACTIVATE = 'inactivate',
}

export interface BusinessPolicyTargetingDTO {
  customerIds: string[];
  customerSetOperator: BusinessPolicySetOperator;
  customerClassificationIds: string[];
  customerClassificationSetOperator: BusinessPolicySetOperator;
  customerStateIds: string[];
  customerStateSetOperator: BusinessPolicySetOperator;
  customerConditionsLogicalOperator: BusinessPolicyLogicalOperatorType;
  orderTypes: OrderType[];
  deliveryWindowIds: string[];
  productClassificationIds: string[];
  collectionIds: string[];
}

export interface BusinessPolicyReferenceDTO {
  id: string;
  name: string;
  code?: string;
  classificationType?: string;
}

export interface BusinessPolicyTargetingReferencesDTO {
  deliveryWindows?: BusinessPolicyReferenceDTO[];
  productClassifications?: BusinessPolicyReferenceDTO[];
  collections?: BusinessPolicyReferenceDTO[];
}

export interface BusinessPolicyRewardDTO {
  position: number;
  condition: BusinessPolicyConditionNodeDTO;
  value: number | null;
  paymentMethodIds: string[];
}

export interface BusinessPolicyBaseDTO {
  id?: string;
  name: string;
  integrationCode?: number | null;
  active: boolean;
  startDate: string | null;
  endDate: string | null;
  targeting: BusinessPolicyTargetingDTO;
  rewards: BusinessPolicyRewardDTO[];
  createdAt?: Date;
  targetingReferences?: BusinessPolicyTargetingReferencesDTO;
}

interface OrderItemDiscountBusinessPolicyDTO extends BusinessPolicyBaseDTO {
  type: BusinessPolicyType.ORDER_ITEM_DISCOUNT;
  discountType: BusinessPolicyDiscountType;
  isCumulative: boolean;
  isAutomatic: boolean;
  applyDiscountOnDiscountedItems: boolean;
  discountApplicationRule: DiscountApplicationRuleEnum;
}

interface VoucherBusinessPolicyDTO extends BusinessPolicyBaseDTO {
  type: BusinessPolicyType.ORDER_VOUCHER;
  discountType: BusinessPolicyDiscountType;
  isCumulative: boolean;
  applyDiscountOnDiscountedItems: boolean;
  usageLimit: number | null;
  usageLimitPerCustomer: number | null;
  blockedRoles: UserRole[];
}

export type CashbackVoucherTemplateDTO = Omit<
  VoucherBusinessPolicyDTO,
  'targeting' | 'targetingReferences'
>;

interface CashbackBusinessPolicyDTO extends BusinessPolicyBaseDTO {
  type: BusinessPolicyType.CASHBACK;
  discountType: BusinessPolicyDiscountType;
  activateOnOrderStatus: StatusType;
  startsAfterHours: number;
  expiresAfterHours: number;
  usageLimitPerCustomer: number | null;
  voucherTemplate?: CashbackVoucherTemplateDTO;
}

interface RestrictionBusinessPolicyDTO extends BusinessPolicyBaseDTO {
  type:
    | BusinessPolicyType.ORDER_RESTRICTION
    | BusinessPolicyType.ORDER_WARNING
    | BusinessPolicyType.ORDER_PRODUCTS_RESTRICTION;
  roles: UserRole[];
}

interface ReleasePaymentMethodsBusinessPolicyDTO extends BusinessPolicyBaseDTO {
  type: BusinessPolicyType.RELEASE_PAYMENT_METHODS;
}

export type BusinessPolicyDTO =
  | OrderItemDiscountBusinessPolicyDTO
  | VoucherBusinessPolicyDTO
  | CashbackBusinessPolicyDTO
  | RestrictionBusinessPolicyDTO
  | ReleasePaymentMethodsBusinessPolicyDTO;

export interface GetBusinessPolicyDTO {
  id: string;
  name: string;
  active: boolean;
  createdAt: Date;
  startDate: string | null;
  endDate: string | null;
  integrationCode?: number | null;
  type: BusinessPolicyType;
}

export interface CopyBusinessPolicyDTO {
  name?: string;
}

export interface BusinessPolicyListFilterParams {
  codeOrName?: string;
  types?: BusinessPolicyType[];
  active?: BusinessPolicyActiveFilter;
  createdAtStart?: string;
  createdAtEnd?: string;
  customerIds?: string[];
  customerStateIds?: string[];
  customerClassificationIds?: string[];
  orderTypes?: CompactAvailability[];
  deliveryWindowIds?: string[];
  orderClassificationIds?: string[];
  orderCollectionIds?: string[];
}

export type FindBusinessPoliciesParams = PaginatedEndpointParams & BusinessPolicyListFilterParams;

export interface BusinessPoliciesBatchSelection {
  businessPolicyIds?: string[];
  massActionFilter: boolean;
  filter?: BusinessPolicyListFilterParams;
}

export interface BatchBusinessPoliciesBody extends BusinessPoliciesBatchSelection {
  batchAction: BusinessPolicyBatchAction;
}

export interface BusinessPolicyVoucherDTO {
  id: string;
  name: string;
  integrationCode?: number | null;
  active: boolean;
  discountType: BusinessPolicyDiscountType;
  isCumulative: boolean;
  applyDiscountOnDiscountedItems: boolean;
  startDate: string | null;
  endDate: string | null;
  usageLimit: number | null;
  usageLimitPerCustomer: number | null;
  blockedRoles: UserRole[];
  discountValue: number | null;
}

export interface FindAvailableVouchersParams extends PaginatedEndpointParams {
  orderId?: string;
}
